// src/hooks/usePermissionTree.js
import { useEffect, useState, useCallback } from "react";

/**
 * usePermissionTree
 * Normaliza el árbol, mantiene estados checked y expanded, 
 * y expone las funciones para interactuar con módulos y acciones.
 */

const keyMod = (id) => `mod-${id}`;
const keyAcc = (id) => `acc-${id}`;

// 🔥 NORMALIZADOR — CORRECCIÓN PRINCIPAL
const normalizeTree = (nodes) =>
  nodes.map(node => ({
    ...node,
    acciones: Array.isArray(node.acciones) ? node.acciones : [],
    children: Array.isArray(node.children)
      ? normalizeTree(node.children)
      : [], // ← garantía de que SIEMPRE hay array
  }));

// recolecta descendientes
const collectAllDescendants = (node, accMods = [], accAccs = []) => {
  accMods.push(node.id_modulo);
  if (node.acciones.length) {
    node.acciones.forEach(a => accAccs.push(a.id_accion));
  }
  if (node.children.length) {
    node.children.forEach(child =>
      collectAllDescendants(child, accMods, accAccs)
    );
  }
  return { mods: accMods, accs: accAccs };
};

export default function usePermissionTree(rawTree = []) {
  const [tree, setTree] = useState([]);
  const [checked, setChecked] = useState(new Map());
  const [expanded, setExpanded] = useState(new Map());

  // load + normalize
  useEffect(() => {
    const normalized = normalizeTree(rawTree);
    setTree(normalized);

    const c = new Map();
    const e = new Map();

    const walk = (nodes) => {
      nodes.forEach(node => {
        c.set(keyMod(node.id_modulo), false);
        e.set(keyMod(node.id_modulo), false);

        node.acciones.forEach(a =>
          c.set(keyAcc(a.id_accion), false)
        );

        if (node.children.length) walk(node.children);
      });
    };

    walk(normalized);
    setChecked(c);
    setExpanded(e);
  }, [rawTree]);

  // helpers (sin cambios)
  const setCheckedKey = useCallback((k, v) => {
    setChecked(prev => {
      const copy = new Map(prev);
      copy.set(k, v);
      return copy;
    });
  }, []);

  const toggleModule = useCallback(
    (modNode) => {
      const k = keyMod(modNode.id_modulo);
      const newVal = !checked.get(k);
      setCheckedKey(k, newVal);

      const { mods, accs } = collectAllDescendants(modNode, [], []);
      mods.forEach(mId => setCheckedKey(keyMod(mId), newVal));
      accs.forEach(aId => setCheckedKey(keyAcc(aId), newVal));
    },
    [checked, setCheckedKey]
  );

  const toggleAction = useCallback(
    (action, parentModuleStack) => {
      const k = keyAcc(action.id_accion);
      const newVal = !checked.get(k);
      setCheckedKey(k, newVal);

      if (newVal) {
        parentModuleStack.forEach(m =>
          setCheckedKey(keyMod(m.id_modulo), true)
        );
      }

      // cleanup
      setTimeout(() => {
        const hasCheckedDescendant = (node) => {
          if (checked.get(keyMod(node.id_modulo))) return true;
          if (node.acciones.some(a => checked.get(keyAcc(a.id_accion))))
            return true;
          return node.children.some(ch => hasCheckedDescendant(ch));
        };

        const cleanup = (nodes) => {
          nodes.forEach(n => {
            if (!hasCheckedDescendant(n))
              setCheckedKey(keyMod(n.id_modulo), false);
            if (n.children.length) cleanup(n.children);
          });
        };

        cleanup(tree);
      }, 10);
    },
    [checked, setCheckedKey, tree]
  );

  const toggleExpand = useCallback((modId) => {
    const k = keyMod(modId);
    setExpanded(prev => {
      const copy = new Map(prev);
      copy.set(k, !copy.get(k));
      return copy;
    });
  }, []);

  // Marca según IDs de acciones que vienen desde BD
  const setCheckedFromIds = useCallback(
    (actionIds = []) => {
      setChecked(prev => {
        const copy = new Map(prev);

        // Reset
        copy.forEach((v, k) => copy.set(k, false));

        // Marca acciones que vienen en los IDs
        actionIds.forEach(id =>
          copy.set(keyAcc(id), true)
        );

        // Recorre árbol y marca módulos hacia arriba
        const markParents = (nodes) => {
          nodes.forEach(node => {
            if (node.children.length) markParents(node.children);

            const anyActive =
              node.acciones.some(a => copy.get(keyAcc(a.id_accion))) ||
              node.children.some(ch => copy.get(keyMod(ch.id_modulo)));

            if (anyActive)
              copy.set(keyMod(node.id_modulo), true);
          });
        };

        markParents(tree);
        return copy;
      });
    },
    [tree]
  );


  const getSelectedPayload = useCallback(() => {
    const mods = [];
    const accs = [];
    checked.forEach((v, k) => {
      if (!v) return;
      if (k.startsWith("mod-"))
        mods.push(parseInt(k.replace("mod-", ""), 10));
      if (k.startsWith("acc-"))
        accs.push(parseInt(k.replace("acc-", ""), 10));
    });
    return { modules: [...new Set(mods)], acciones: [...new Set(accs)] };
  }, [checked]);

  return {
    tree,
    checked,
    expanded,
    toggleModule,
    toggleAction,
    toggleExpand,
    setCheckedFromIds,
    getSelectedPayload,
    setTree,
  };
}
